/**
 * Drop noisy client errors caused by third-party marketing / attribution pixels
 * when the user network, region, or ad-blocker blocks those hosts (common in CN / corporate networks).
 *
 * These are not application defects — filtering reduces Datadog signal-to-noise ratio.
 */

/** URL/host substrings and paths seen on failed pixel / conversion loads */
const MARKETING_TRACKING_URL_PATTERNS: RegExp[] = [
    /facebook\.com\/(?:tr\/|privacy_sandbox\/)/i,
    /connect\.facebook\.net/i,
    /analytics\.twitter\.com/i,
    /\/pagead\//i,
    /googletagmanager\.com/i,
    /googleadservices\.com/i,
    /doubleclick\.net/i,
    /linkedin\.com\/px/i,
    /snapchat\.com\/p/i,
    /tiktok\.com\/i18n\/pixel/i,
    /bat\.bing\.com/i,
];

/** First-party telemetry delivery URLs must never generate telemetry about themselves. */
const TELEMETRY_TRANSPORT_URL_PATTERNS: RegExp[] = [
    /event\.converge\.ai\/v1\//i,
    /datadog-proxy\.framia\.pro/i,
    /[?&]ddforward=/i,
];

const CONNECTION_NOISE_SUBSTRINGS = [
    'net::ERR_CONNECTION_REFUSED',
    'net::ERR_CONNECTION_TIMED_OUT',
    'net::ERR_TIMED_OUT',
    'net::ERR_CONNECTION_RESET',
    'net::ERR_NAME_NOT_RESOLVED',
    'Failed to load resource',
    'Resource load failed',
    'Load failed',
    'request timed out',
    'timed out',
];

function safeStringify(value: unknown, maxLen = 12000): string {
    try {
        const s = typeof value === 'string' ? value : JSON.stringify(value);
        return s.length > maxLen ? s.slice(0, maxLen) : s;
    } catch {
        return String(value);
    }
}

type RumResourceShape = {
    url?: string;
    /** 0 = failed / aborted / timeout in many browsers */
    status_code?: number;
};

/**
 * Collect URLs and error text RUM may put outside the top-level JSON blob
 * (e.g. `resource.url` for resource timeout / failure).
 */
function augmentRumEventTextForFilter(event: unknown): string {
    const base = safeStringify(event);
    if (!event || typeof event !== 'object') return base;
    const e = event as Record<string, unknown>;
    const extra: string[] = [];

    const push = (v: unknown) => {
        if (typeof v === 'string') extra.push(v);
    };

    push(e.url);
    if (e.resource && typeof e.resource === 'object') {
        const r = e.resource as RumResourceShape;
        push(r.url);
    }
    if (e.http && typeof e.http === 'object') {
        push((e.http as { url?: string }).url);
    }
    if (e.error && typeof e.error === 'object') {
        const err = e.error as { message?: string; stack?: string; source?: string };
        push(err.message);
        push(err.stack);
        push(err.source);
    }

    return [base, ...extra].filter(Boolean).join('\n');
}

/**
 * RUM `resource` events: marketing/tracking URL with failed status (incl. timeout → often 0).
 * Does not drop successful (2xx) third-party beacons.
 */
function rumMarketingResourceFailure(event: unknown): boolean {
    if (!event || typeof event !== 'object') return false;
    const e = event as { type?: string; resource?: RumResourceShape };
    if (e.type !== 'resource' || !e.resource || typeof e.resource !== 'object') return false;
    const url = e.resource.url;
    if (typeof url !== 'string' || !textMatchesMarketingTrackingNoise(url)) return false;
    const code = e.resource.status_code;
    return code === 0 || (typeof code === 'number' && code >= 400);
}

/**
 * True if `text` references a known marketing/tracking endpoint (any failure reason).
 */
export function textMatchesMarketingTrackingNoise(text: string | undefined | null): boolean {
    if (!text) return false;
    const t = text.toLowerCase();
    return MARKETING_TRACKING_URL_PATTERNS.some((re) => re.test(t));
}

/** True if `text` references one of our telemetry delivery endpoints. */
export function textMatchesTelemetryTransport(text: string | undefined | null): boolean {
    if (!text) return false;
    return TELEMETRY_TRANSPORT_URL_PATTERNS.some((re) => re.test(text));
}

/**
 * True if the log looks like a connectivity failure tied to marketing/tracking URLs.
 */
export function textLooksLikeBlockedMarketingRequest(text: string | undefined | null): boolean {
    if (!text) return false;
    const hasConnNoise = CONNECTION_NOISE_SUBSTRINGS.some((s) => text.includes(s));
    if (!hasConnNoise) return false;
    return textMatchesMarketingTrackingNoise(text);
}

type LogsEventShape = {
    message?: string;
    error?: {
        message?: string;
        stack?: string;
        causes?: Array<{ message?: string; stack?: string }>;
    };
    http?: { url?: string };
};

/**
 * Filter for @datadog/browser-logs `beforeSend`.
 * @returns true if the log should be **dropped** (do not send to Datadog).
 */
export function shouldDropDatadogLogsEvent(event: unknown): boolean {
    if (!event || typeof event !== 'object') return false;
    const e = event as LogsEventShape;
    const parts: string[] = [e.message ?? ''];

    if (e.error) {
        parts.push(e.error.message ?? '', e.error.stack ?? '');
        for (const c of e.error.causes ?? []) {
            parts.push(c.message ?? '', c.stack ?? '');
        }
    }
    if (e.http?.url) parts.push(e.http.url);

    const combined = parts.filter(Boolean).join('\n');
    if (textMatchesTelemetryTransport(combined)) return true;
    if (textMatchesMarketingTrackingNoise(combined)) return true;
    if (textLooksLikeBlockedMarketingRequest(combined)) return true;
    return false;
}

/**
 * Filter for @datadog/browser-rum `beforeSend` on resource/error types.
 * @returns true if the RUM event should be **dropped**.
 */
export function shouldDropDatadogRumEvent(event: unknown): boolean {
    if (!event || typeof event !== 'object') return false;
    const ev = event as { type?: string };
    if (ev.type !== 'error' && ev.type !== 'resource') return false;

    // console.error 会被 SDK 无条件转为 error.source='console' 的 RUM error，
    // 与显式 addError/trackAnalyticsError 双份上报；console 噪音（预期错误、
    // 第三方脚本）不应占 error 面板，统一丢弃
    if (ev.type === 'error' && (event as { error?: { source?: string } }).error?.source === 'console') {
        return true;
    }

    if (rumMarketingResourceFailure(event)) return true;

    const blob = augmentRumEventTextForFilter(event);
    if (textMatchesTelemetryTransport(blob)) return true;
    if (textMatchesMarketingTrackingNoise(blob)) return true;
    if (textLooksLikeBlockedMarketingRequest(blob)) return true;
    return false;
}

/**
 * Used by the analytics Error integration: drop captured errors that only concern blocked pixels.
 */
export function shouldDropCapturedBrowserError(payload: {
    error_message?: string;
    error_stack?: string;
    source?: string;
}): boolean {
    const combined = [payload.error_message, payload.error_stack, payload.source].filter(Boolean).join('\n');
    if (textMatchesTelemetryTransport(combined)) return true;
    if (textMatchesMarketingTrackingNoise(combined)) return true;
    if (textLooksLikeBlockedMarketingRequest(combined)) return true;
    return false;
}
